import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { BlogDetailsPage, BlogDetailsPageCollection, BlogList as TBlogList } from '@/types/generated';
import { ICardDesignType } from './BlogListItem';
import BlogListPageDefault from './BlogListPageDefault/BlogListPageDefault';
import { blogListTitlerContainerStyles } from './BlogListPageDefault/BlogListPageDefault.styles';
import BlogsHalfScreen from './BlogsHalfScreen/BlogsHalfScreen';
import BlogsListHero from './BlogsListHero/BlogsListHero';
import { useAppContext } from '@shared-components/context/AppContextProvider.ctx';
import { getBlogListByBrand } from '@shared-components/services/getBlogListByBrand';
import { Maybe } from '@shared-components/types';
import { getNumberOfBlogs } from '@utils';
import { logWarning } from '@utils/miscUtils';
export type BlogListResponse = {
  blogDetailsPageCollection: BlogDetailsPageCollection;
  total: number;
  skip: number;
  limit: number;
};
export interface IBlogList extends TBlogList {
  /**
   * Used while fetching the blog list.
   *
   * @type {Boolean}
   * @memberof IBlogList
   */
  preview?: boolean;
  cardDesignType?: ICardDesignType;
  startIndexBlogList: number;
  setStartIndexBlogList: (startIndex: number) => void;
  categoryName?: string;
  subCategoryName?: string;
  tags?: Array<string>;
}
export const getPaginationPageIndex = (pageNumber: number, countPerPage: number) => {
  return (pageNumber - 1) * countPerPage;
};
const getBlogListPagePath = (root: string, page: number) => `${root.replace(/\/$/, '')}/${page}`;
const getBlogTagPagePath = (pathname: string, page: number) => {
  const trimmedPathname = pathname.replace(/\/$/, '');
  const pathParts = trimmedPathname.split('/');
  const lastPathPart = pathParts.at(-1) ?? '';
  const basePath = /^\d+$/.test(lastPathPart) ? pathParts.slice(0, -1).join('/') : trimmedPathname;
  return `${basePath}/${page}`;
};

// Falls back to the non-tag path when window isn't available (e.g. during SSR).
const getBlogListPageHref = (root: string, page: number) => {
  if (globalThis.window === undefined) {
    return getBlogListPagePath(root, page);
  }
  return globalThis.window.location.pathname.includes('/tag/') ? getBlogTagPagePath(globalThis.window.location.pathname, page) : getBlogListPagePath(root, page);
};
const updateBlogListPageRoute = (root: string, page: number) => {
  if (!globalThis.window?.history) {
    return;
  }
  globalThis.window.history.pushState(null, '', getBlogListPageHref(root, page));
};
const BlogListTitle = ({
  title
}: {
  title?: Maybe<string>;
}) => {
  return <>
			{title && <Box sx={blogListTitlerContainerStyles}>
					<Typography component="h3" variant="header2" color="text.primary" data-testid="blog_list_title">
						{title}
					</Typography>
				</Box>}
		</>;
};
export default function BlogList({
  title,
  displayType,
  cardDesignType,
  brand,
  buttonLabel,
  loadMoreButtonLabel,
  preview = false,
  heroBlogsListCollection,
  countAcrossDesktop,
  countAcrossMobile,
  hasPagination = false,
  featuredBlogsListCollection,
  startIndexBlogList = 0,
  setStartIndexBlogList,
  categoryName,
  subCategoryName,
  tagsFilter,
  tags
}: Readonly<IBlogList>) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isFeatured = displayType === 'Featured';
  const isTagType = displayType === 'Tag';
  const {
    environment,
    config
  } = useAppContext();
  const blogFeatureConfig = config.features?.blogs;
  const blogRootPath = blogFeatureConfig?.root ?? '/';
  const numberOfBlogs = getNumberOfBlogs(displayType as string, blogFeatureConfig);
  const [state, setState] = useState<{
    totalBlogs: number;
    displayShowMoreButton: boolean;
    blogsList: Array<BlogDetailsPage>;
  }>({
    totalBlogs: 0,
    blogsList: [],
    displayShowMoreButton: false
  });
  const contentfulGraphqlURL = environment === 'prod' ? config.services.prod.CONTENTFUL_GRAPHQL_URL : config.services.nonprod.CONTENTFUL_GRAPHQL_URL;

  // brand name comes from blog list content itself or take it from context
  const brandNameForQuery = brand?.title ?? config.name;
  const getStateReset = () => ({
    ...state,
    blogsList: [],
    displayShowMoreButton: false,
    totalBlogs: 0
  });
  let filterByTags: Array<string> | undefined;
  if (isTagType) {
    filterByTags = tagsFilter?.length ? tagsFilter as Array<string> : tags;
  } else {
    filterByTags = undefined;
  }
  useEffect(() => {
    const featuredBlogsList = featuredBlogsListCollection?.items as BlogDetailsPage[] || [];
    if (isFeatured) {
      setState({
        ...state,
        blogsList: featuredBlogsList,
        displayShowMoreButton: false,
        totalBlogs: featuredBlogsListCollection?.items.length ?? 2
      });
      return;
    }
    setIsLoading(true);
    getBlogListByBrand({
      limit: numberOfBlogs,
      skip: startIndexBlogList,
      brandName: brandNameForQuery,
      url: contentfulGraphqlURL,
      preview,
      categoryName,
      subCategoryName,
      tags: filterByTags
    }).then((blogsListResponse: BlogListResponse | undefined) => {
      if (blogsListResponse) {
        const blogDetailsPageItems = blogsListResponse.blogDetailsPageCollection?.items as Array<BlogDetailsPage>;
        const totalBlogs = blogsListResponse.blogDetailsPageCollection?.total ?? 0;
        const shouldAppendBlogs = !hasPagination && startIndexBlogList !== 0;
        const blogDetailsList = shouldAppendBlogs ? state.blogsList.concat(blogDetailsPageItems) : blogDetailsPageItems;
        setState({
          ...state,
          blogsList: blogDetailsList,
          displayShowMoreButton: displayType === 'Blog List Page' && blogDetailsList.length < totalBlogs,
          totalBlogs
        });
      }
      setIsLoading(false);
      return blogsListResponse;
    }).catch(error => {
      // Handle any errors that occurred during the API call.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      logWarning(`Error fetching blogs list by brand error: ${error}`);
      setState(getStateReset());
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndexBlogList, categoryName, subCategoryName]);
  const handleShowMoreClick = () => {
    setStartIndexBlogList(startIndexBlogList + numberOfBlogs);
    setState({
      ...state,
      displayShowMoreButton: false
    });
  };
  const getPageHref = (page: number) => getBlogListPageHref(blogRootPath, page);
  const handlePaginationChangeEvent = (e: React.ChangeEvent<unknown>, page: number) => {
    const indexByPage = getPaginationPageIndex(page, numberOfBlogs);
    if (indexByPage === startIndexBlogList) {
      return;
    }
    if (displayType === 'Blog List Page' || displayType === 'Tag') {
      updateBlogListPageRoute(blogRootPath, page);
    }
    globalThis.window.scrollTo({
      top: 0
    });
    setState({
      ...state,
      blogsList: [],
      displayShowMoreButton: false,
      totalBlogs: 0
    });
    setIsLoading(true);
    setStartIndexBlogList(indexByPage);
  };
  switch (displayType) {
    case 'Half Screen Left':
    case 'Half Screen Right':
      return <BlogsHalfScreen displayType={displayType} blogsList={state.blogsList} buttonLabel={buttonLabel as string} loadMoreButtonLabel={loadMoreButtonLabel as string} />;
    case 'Hero':
      return <BlogsListHero blogsList={heroBlogsListCollection?.items as Array<BlogDetailsPage>} buttonLabel={buttonLabel} />;
    default:
      return <Box sx={{
        width: '100%'
      }}>
					<BlogListTitle title={title} />
					<BlogListPageDefault totalBlogs={state.totalBlogs} blogsList={state.blogsList} buttonLabel={buttonLabel as string} loadMoreButtonLabel={loadMoreButtonLabel as string} displayShowMoreButton={state.displayShowMoreButton} handleShowMoreClick={handleShowMoreClick} handlePaginationChangeEvent={handlePaginationChangeEvent} getPageHref={getPageHref} cardDesignType={cardDesignType} countAcrossDesktop={countAcrossDesktop} countAcrossMobile={countAcrossMobile} hasPagination={hasPagination} limit={numberOfBlogs} displayType={displayType} isLoading={isLoading} currentPage={Math.floor(startIndexBlogList / numberOfBlogs) + 1} />
				</Box>;
  }
}